/**
 * Pure, DOM-free geometry for mapping browser coordinates onto the shared display.
 *
 * The `<video>` is rendered with `object-fit: contain`, so it is letterboxed inside its
 * container: to reach the shared screen's right/bottom edge you must reach the VIDEO's
 * edge, not the container's. Normalizing against the container rect (the old bug) forced
 * the user to move into the letterbox bars — i.e. to the far edge of the window — to hit
 * the edge of the remote screen. These helpers normalize against the actual displayed
 * video rect instead, so edges line up at any window size or aspect ratio.
 */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The displayed rectangle of a `srcW×srcH` source inside container `c` under
 * `object-fit: contain` (scaled to fit, centered, letterboxed). Falls back to the full
 * container if any dimension is unknown (e.g. video metadata not loaded yet).
 */
export function containRect(c: Rect, srcW: number, srcH: number): Rect {
  if (srcW <= 0 || srcH <= 0 || c.width <= 0 || c.height <= 0) return c;
  const containerAR = c.width / c.height;
  const sourceAR = srcW / srcH;
  let width: number;
  let height: number;
  if (sourceAR > containerAR) {
    // source is wider than the box -> fit width, letterbox top/bottom
    width = c.width;
    height = c.width / sourceAR;
  } else {
    // source is taller/narrower -> fit height, pillarbox left/right
    height = c.height;
    width = c.height * sourceAR;
  }
  return {
    left: c.left + (c.width - width) / 2,
    top: c.top + (c.height - height) / 2,
    width,
    height,
  };
}

/** Normalize a client point into [0,1]×[0,1] within `content`, clamped to the edges. */
export function normalizeToContent(clientX: number, clientY: number, content: Rect): { x: number; y: number } {
  const x = content.width > 0 ? (clientX - content.left) / content.width : 0;
  const y = content.height > 0 ? (clientY - content.top) / content.height : 0;
  return {
    x: Math.min(1, Math.max(0, Number(x.toFixed(5)))),
    y: Math.min(1, Math.max(0, Number(y.toFixed(5)))),
  };
}
